import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spinner, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, Button } from '@fluentui/react-components';
import { Navigation24Regular, MoreVertical24Regular, ArrowDownload24Regular, ArrowReset24Regular, Edit24Regular, ArrowMove24Regular, Delete24Regular, DocumentTable24Regular } from '@fluentui/react-icons';
import { exportToPDFWithHeaderFooter } from '../../Utils/pdfExport';
import { exportWidgetToExcel } from '../../Utils/excelExport';
import { fetchWidgetData } from '../../Services/AnalyticsService';
import { mapAnalyticsData } from '../../Utils/dimensionsMapper';
import PieChartWidget from './PieChartWidget';
import DonutChartWidget from './DonutChartWidget';
import BarChartWidget from './BarChartWidget';
import LineChartWidget from './LineChartWidget';
import AreaChartWidget from './AreaChartWidget';
import ScatterChartWidget from './ScatterChartWidget';
import RadarChartWidget from './RadarChartWidget';
import GaugeWidget from './GaugeWidget';
import TableWidget from './TableWidget';
import MetricWidget from './MetricWidget';
import FunnelChartWidget from './FunnelChartWidget';
// Fluent UI Widgets
import GaugeFluentWidget from './GaugeFluentWidget';
import BarFluentWidget from './BarFluentWidget';
import DonutFluentWidget from './DonutFluentWidget';
import HorizontalBarFluentWidget from './HorizontalBarFluentWidget';
import DailySlaWidget from './DailySlaWidget';
import DailyAbandonWidget from './DailyAbandonWidget';
import AgentStatusWidget from './AgentStatusWidget';
import AgentStatusBreakdownWidget from './AgentStatusBreakdownWidget';
import IvrOptionDistributionWidget from './IvrOptionDistributionWidget';
import IvrStatsTableWidget from './IvrStatsTableWidget';
import IvrFunnelWidget from './IvrFunnelWidget';
import WordCloudWidget from './WordCloudWidget';
import { WidgetTypes } from '../../Types/widgetTypes';
import './Widget.css';

const Widget = ({
    config,
    filterValues = {},
    isEditMode = false,
    tenantId,
    token,
    preloadedData = null,  // Données déjà chargées par le Dashboard (optimisation)
    isLoading = false,     // État de chargement global du Dashboard
    onRestore = null,      // Fonction pour restaurer le widget à sa position/taille par défaut
    onEdit = null,         // Fonction pour créer une instance personnalisée du widget
    onMove = null,         // Fonction pour déplacer/copier vers un autre tab
    onDelete = null,       // Fonction pour masquer le widget du Dashboard
    // Drill-down : ouvre la liste d'appels filtrée sur la population que compte CE widget.
    // Fourni uniquement pour les cartes de comptage dont la définition correspond
    // exactement à un sous-ensemble d'appels (cf. CALL_DRILLDOWNS côté AnalyticsDashboard).
    onDrillDown = null,
    // Pastille « ce widget a ses PROPRES filtres » (période figée, flows/files/agents choisis à
    // la config). Deux copies du même widget peuvent coexister dans un onglet, l'une filtrée et
    // l'autre non, sans rien pour les distinguer : la donnée affichée n'était alors plus
    // rattachable aux filtres du rapport. Construite par AnalyticsDashboard, rendue ici.
    filterBadge = null
}) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [localLoading, setLocalLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    // Get dimensions from Redux for mapping UUIDs to names
    const agents = useSelector(state => state.reducer.AGENTS);
    const flows = useSelector(state => state.reducer.FLOWS);
    const queues = useSelector(state => state.reducer.QUEUES);

    // Si `preloadedData` est disponible, prioriser le `localLoading` (sera false dès réception)
    // Sinon, utiliser `isLoading` du Dashboard pour les widgets avec queryId/reportConfig
    const loading = (preloadedData !== null && preloadedData !== undefined)
        ? localLoading
        : (config.config?.dataSource === 'reportConfig' || config.queryId ? isLoading : localLoading);

    // Si des données pré-chargées sont fournies, les utiliser directement
    useEffect(() => {
        if (preloadedData !== null && preloadedData !== undefined) {
            // Pour les widgets Analytics Profile (Fluent), ne pas mapper les données
            // car ce sont déjà des données mockées au bon format
            if (config.type?.includes('fluent') || config.type?.includes('FLUENT')) {
                setData(preloadedData);
                setLocalLoading(false);
                return;
            }

            // Map UUIDs to human-readable names using Redux dimensions
            const mappedResult = mapAnalyticsData(preloadedData, { agents, flows, queues });

            setData(mappedResult);
            setLocalLoading(false);
        } else if (!config.queryId && !config.config?.dataSource) {
            // Pas de queryId ni dataSource - pas de données à charger
            setData(null);
            setLocalLoading(false);
        }
        // Si le widget a un queryId ou un dataSource, ne PAS fetch ici :
        // le dashboard parent va envoyer le preloadedData. Faire un fetch ici
        // double les requêtes au mount initial (chaque widget fetch en plus du dashboard).
    }, [preloadedData, agents, flows, queues]);

    // Ancien useEffect pour les widgets qui ne reçoivent pas de preloadedData
    useEffect(() => {
        if (preloadedData === null || preloadedData === undefined) {
            if (config.queryId || config.config?.dataSource === 'reportConfig') {
                // Si le widget a un queryId ou utilise reportConfig, attendre le preloadedData du Dashboard
                // Le loading est géré par isLoading du Dashboard
                return;
            }
            fetchData();
        }
    }, [config, filterValues]);

    const fetchData = async () => {
        setLocalLoading(true);

        try {
            // Check if widget has a queryId (real data from analytics API)
            if (config.queryId && tenantId) {
                const result = await fetchWidgetData(config, filterValues, tenantId, token);

                // Map UUIDs to human-readable names using Redux dimensions
                const mappedResult = mapAnalyticsData(result, { agents, flows, queues });

                setData(mappedResult);
                setLocalLoading(false);
            } else {
                // No queryId or tenantId - set empty state
                setData(null);
                setLocalLoading(false);
            }
        } catch (err) {
            // Set empty state on error
            setData(null);
            setLocalLoading(false);
        }
    };

    const renderWidget = () => {
        if (loading) {
            // Display spinner during loading
            return (
                <div className="widget-loading-state">
                    <Spinner size="large"  />
                </div>
            );
        }

        // Empty state when no data available
        // Special case: for objects with 'value' property (like metric widgets), check if value property exists
        const isError = data && typeof data === 'object' && !Array.isArray(data) && 'error' in data;
        const hasValue = data && typeof data === 'object' && !Array.isArray(data) && !isError && 'value' in data;

        // Check if array has all zero values (for charts only — tables have custom column keys)
        const isAllZero = Array.isArray(data) && data.length > 0 &&
            config.type !== WidgetTypes.TABLE && config.type !== WidgetTypes.WORD_CLOUD && config.type !== WidgetTypes.SLA_TABLE && config.type !== WidgetTypes.ABANDON_TABLE && config.type !== WidgetTypes.AGENT_STATUS_TABLE && config.type !== WidgetTypes.AGENT_STATUS_BREAKDOWN && config.type !== WidgetTypes.IVR_OPTION_DISTRIBUTION && config.type !== WidgetTypes.IVR_STATS_TABLE && config.type !== WidgetTypes.IVR_FUNNEL &&
            data.every(item => {
                const hasNumericKey = ['value', 'count', 'callCount', 'y'].some(k => item[k] !== undefined);
                if (!hasNumericKey) return false;
                const value = item.value ?? item.count ?? item.callCount ?? item.y ?? 0;
                return value === 0;
            });

        const isEmpty = !data ||
            isError ||
            (Array.isArray(data) && data.length === 0) ||
            (typeof data === 'object' && !Array.isArray(data) && !hasValue && !isError && Object.keys(data).length === 0) ||
            isAllZero;

        if (isEmpty) {
            return (
                <div className="widget-empty-state">
                    <div className="empty-icon">📊</div>
                    <div className="empty-message">{t('NO_DATA_FOR_PERIOD')}</div>
                </div>
            );
        }

        switch (config.type) {
            case WidgetTypes.PIE:
                return <PieChartWidget config={config} data={data} />;
            case WidgetTypes.DONUT:
                return <DonutChartWidget config={config} data={data} />;
            case WidgetTypes.BAR:
                return <BarChartWidget config={config} data={data} />;
            case WidgetTypes.LINE:
                return <LineChartWidget config={config} data={data} />;
            case WidgetTypes.AREA:
                return <AreaChartWidget config={config} data={data} />;
            case WidgetTypes.SCATTER:
                return <ScatterChartWidget config={config} data={data} />;
            case WidgetTypes.RADAR:
                return <RadarChartWidget config={config} data={data} />;
            case WidgetTypes.GAUGE:
                return <GaugeWidget config={config} data={data} />;
            case WidgetTypes.TABLE:
                return <TableWidget config={config} data={data} />;
            case WidgetTypes.WORD_CLOUD:
                return <WordCloudWidget data={data} config={config} />;
            case WidgetTypes.FUNNEL:
                return <FunnelChartWidget config={config} data={data} />;
            case WidgetTypes.METRIC:
                return <MetricWidget config={config.config} data={data} />;
            // Fluent UI Chart Types
            case WidgetTypes.GAUGE_FLUENT:
                return <GaugeFluentWidget config={config} data={data} />;
            case WidgetTypes.BAR_FLUENT:
                return <BarFluentWidget config={config} data={data} />;
            case WidgetTypes.DONUT_FLUENT:
                return <DonutFluentWidget config={config} data={data} />;
            case WidgetTypes.HORIZONTAL_BAR_FLUENT:
                return <HorizontalBarFluentWidget config={config} data={data} />;
            case WidgetTypes.SLA_TABLE:
                return <DailySlaWidget config={config} data={data} />;
            case WidgetTypes.ABANDON_TABLE:
                return <DailyAbandonWidget config={config} data={data} />;
            case WidgetTypes.AGENT_STATUS_TABLE:
                return <AgentStatusWidget config={config} data={data} timezone={filterValues?.timezone} />;
            case WidgetTypes.AGENT_STATUS_BREAKDOWN:
                return <AgentStatusBreakdownWidget config={config} data={data} />;
            case WidgetTypes.IVR_OPTION_DISTRIBUTION:
                return <IvrOptionDistributionWidget config={config} data={data} />;
            case WidgetTypes.IVR_STATS_TABLE:
                return <IvrStatsTableWidget config={config} data={data} />;
            case WidgetTypes.IVR_FUNNEL:
                return <IvrFunnelWidget config={config} data={data} />;
            default:
                return <div>Unsupported widget type: {config.type}</div>;
        }
    };

    const handleExportWidget = async () => {
        setIsExporting(true);
        try {
            const now = new Date();
            const dateStr = now.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).replace(/\//g, '-');

            const filename = `${config.title.replace(/\s+/g, '_')}_${dateStr}.pdf`;
            const widgetElementId = `widget-${config.id}`;

            await exportToPDFWithHeaderFooter(widgetElementId, filename, {
                header: {
                    title: config.title,
                    subtitle: config.description || ''
                },
                footer: {
                    pageNumbers: false
                }
            });
        } catch (error) {
            // Error handled silently
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportToExcel = async () => {
        try {
            if (!data) {
                alert(t('NO_DATA_TO_EXPORT') || 'No data to export');
                return;
            }
            setIsExporting(true);
            // Conversion déléguée à Utils/excelExport — SOURCE UNIQUE, partagée avec l'export
            // par onglet. Cette fonction en contenait une COPIE qui ne connaissait que trois
            // types de tableaux ; tous les autres (agent-status-table, ivr-stats-table…)
            // s'exportaient vides.
            exportWidgetToExcel(data, config.title || 'widget', config, t, filterValues?.timezone);
        } catch (error) {
            console.error('Excel export failed:', error);
            alert(`${t('EXPORT_ERROR') || 'Export failed'}: ${error.message}`);
        } finally {
            setIsExporting(false);
        }
    };

    // For metric widgets, match the top accent bar color to the metric status color
    let accentStyle = {};
    if (config.type === WidgetTypes.METRIC && data && !loading) {
        const metricValue = data?.value ?? 0;
        const metricTarget = data?.targetValue;
        const metricMax = data?.maxValue;
        if (metricTarget !== undefined && metricTarget !== null && metricMax !== undefined && metricMax !== null && metricMax > 0) {
            const lowerThreshold = Math.min(metricTarget, metricMax);
            const upperThreshold = Math.max(metricTarget, metricMax);
            if (metricValue < lowerThreshold) {
                accentStyle = { borderTopColor: 'var(--heedify-success)' };
            } else if (metricValue < upperThreshold) {
                accentStyle = { borderTopColor: 'var(--heedify-warning-dark)' };
            } else {
                accentStyle = { borderTopColor: 'var(--heedify-danger)' };
            }
        }
    }

    return (
        <div className="widget-container" id={`widget-${config.id}`} style={accentStyle}>
            <div className="widget-header">
                <h3 className="widget-title">
                    {/* defaultValue : sans lui, une clé i18n manquante s'afficherait BRUTE
                        (« WIDGET_NAMES.widget-xxx ») au lieu du libellé. */}
                    {t(config.titleKey || config.title, { defaultValue: config.title })}
                    {filterBadge}
                </h3>
                <div className="widget-actions">
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <Button
                                appearance="subtle"
                                icon={<MoreVertical24Regular />}
                                size="small"
                            />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                           {isEditMode && (<MenuItem
                                        icon={<Edit24Regular />}
                                        onClick={() => onEdit && onEdit(config.id)}
                                        disabled={!onEdit}
                                    >
                                        {t('EDIT_WIDGET') || 'Edit Widget'}
                                    </MenuItem>
                              )}
                                    
                                <MenuItem
                                    icon={<ArrowDownload24Regular />}
                                    onClick={handleExportToExcel}
                                    disabled={isExporting}
                                >
                                    {isExporting ? t('EXPORTING') : t('EXPORT_EXCEL')}
                                </MenuItem>
                                <MenuItem
                                    icon={<ArrowDownload24Regular />}
                                    onClick={handleExportWidget}
                                    disabled={isExporting}
                                >
                                    {isExporting ? t('EXPORTING') : t('EXPORT_PDF')}
                                </MenuItem>
                               {isEditMode && onDelete && (
                                    <MenuItem
                                        icon={<Delete24Regular />}
                                        onClick={() => onDelete(config.id)}
                                    >
                                        {t('DELETE') || 'Delete'}
                                    </MenuItem>
                                )} 
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            </div>
            {config.description && <p className="widget-description">{config.description}</p>}
            {/* Le drill-down est porté par le CORPS du widget, pas par le conteneur : le header
                contient le menu ⋮ (édition, exports) — un clic dessus ne doit pas basculer
                d'onglet. En mode édition on le désactive aussi, sinon glisser un widget
                déclencherait la navigation au relâchement. */}
            <div
                className={`widget-body${onDrillDown && !isEditMode ? ' widget-body--drilldown' : ''}`}
                onClick={onDrillDown && !isEditMode ? () => onDrillDown(config.id) : undefined}
                role={onDrillDown && !isEditMode ? 'button' : undefined}
                tabIndex={onDrillDown && !isEditMode ? 0 : undefined}
                onKeyDown={onDrillDown && !isEditMode
                    ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDrillDown(config.id); } }
                    : undefined}
                title={onDrillDown && !isEditMode ? t('DRILLDOWN_HINT', { defaultValue: 'Click to see these calls' }) : undefined}
            >
                {renderWidget()}
            </div>
        </div>
    );
};

export default Widget;
